import {
	CSSProperties,
	MouseEvent,
	useCallback,
	useContext,
	useSyncExternalStore,
} from 'react';
import { toast } from 'sonner';

import { AppContext } from '@/app.context';
import { queryKeys } from '@shared/api';
import { defaultQueryClient } from '@shared/query-client';
import { translate, type MessageKey } from '@shared/i18n';

/**
 * The offline affordance every template's issue covers share: while the
 * reader is offline, a cover whose destination page has no data in the
 * TanStack Query cache -- nothing to show without a network round trip --
 * is greyed out (grayscale + 50% opacity; grayscale alone reads as "a
 * duller cover", not "unavailable"), and tapping it shows a toast instead
 * of navigating into a page that can only fail.
 *
 * "Cached" is asked of the page the cover actually opens (`target`):
 *
 * - `'issue'` -- the reader, `/issues/$id` (`queryKeys.issue`, matched by
 *   prefix so any `limit`/`sortKey`/`reverse` variant counts).
 * - `'preview'` -- a preview screen, `/preview/$id` (`queryKeys.preview`),
 *   which every preview page reads (`useGetIssue`, `reader/preview`).
 *
 * Only the implicit cache counts: downloaded issues (Dexie) open through
 * their own `/downloads/issues/*` route, never through these covers.
 *
 * Callers must also pass `preload={false}` to the cover's link: the
 * router's hover/touchstart prefetch (`defaultPreload: 'intent'`) would
 * cache issues the reader never opened, and "not greyed out" would stop
 * meaning "readable offline".
 *
 * Styling: `coverStyle` sets `filter`/`opacity` inline; the fade itself is
 * the cover's own CSS `transition` (add `OFFLINE_COVER_TRANSITION` to it),
 * because an inline `transition` would override any transition a template
 * already has on that element (hover scale, shadow).
 */
export type OfflineCoverTarget = 'issue' | 'preview';

/** CSS transition entries for a cover's own stylesheet. */
export const OFFLINE_COVER_TRANSITION =
	'filter 220ms ease-out, opacity 220ms ease-out';

/** The toast a blocked offline cover shows (`issue.offlineCover`). */
export const OFFLINE_COVER_MESSAGE = 'issue.offlineCover' satisfies MessageKey;

function queryKeyFor(id: string | number, target: OfflineCoverTarget) {
	return target === 'preview' ? queryKeys.preview(id) : queryKeys.issue(id);
}

/**
 * Whether the page a cover opens (`target`) has data in the query cache for
 * this issue. Reactive to the query cache (`useSyncExternalStore`), so a
 * cover updates when an issue is fetched or garbage-collected without a
 * reload. `.some(data !== undefined)` rather than "an entry exists": an
 * entry can be registered mid-flight or after a failure with no data.
 */
export function useIsIssueCachedOffline(
	id: string | number,
	target: OfflineCoverTarget = 'issue',
): boolean {
	const getSnapshot = useCallback(
		() =>
			defaultQueryClient
				.getQueryCache()
				.findAll({ queryKey: queryKeyFor(id, target), exact: false })
				.some((query) => query.state.data !== undefined),
		[id, target],
	);

	const subscribe = useCallback((onStoreChange: () => void) => {
		return defaultQueryClient.getQueryCache().subscribe(onStoreChange);
	}, []);

	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export interface OfflineCover {
	/** Offline, and the destination page has nothing cached. */
	isUnavailableOffline: boolean;
	/** `filter`/`opacity` for the cover `<img>` (merge into its `style`). */
	coverStyle: CSSProperties;
	/**
	 * Call first in the cover link's `onClick`: when unavailable it cancels
	 * the navigation, shows the toast and returns `true` -- skip the rest
	 * (e.g. tracking) in that case.
	 */
	blockIfUnavailable: (event: MouseEvent) => boolean;
}

/**
 * `useIsIssueCachedOffline` plus connectivity, turned into what a cover
 * renders. `id` is whatever the cover's link uses as its `$id` param
 * (usually `slug ?? id`) -- the same value the destination page keys its
 * query on. Pass `enabled: false` for a cover that doesn't navigate to an
 * issue page at all (e.g. a locked issue), so it's never greyed out.
 */
export function useOfflineCover(
	id: string | number | undefined | null,
	target: OfflineCoverTarget,
	{ enabled = true }: { enabled?: boolean } = {},
): OfflineCover {
	const { onlineStatus } = useContext(AppContext);
	const isCached = useIsIssueCachedOffline(id ?? '', target);
	const isUnavailableOffline =
		enabled &&
		id !== undefined &&
		id !== null &&
		!onlineStatus &&
		!isCached;

	const blockIfUnavailable = useCallback(
		(event: MouseEvent) => {
			if (!isUnavailableOffline) {
				return false;
			}
			event.preventDefault();
			toast.error(translate(OFFLINE_COVER_MESSAGE));
			return true;
		},
		[isUnavailableOffline],
	);

	return {
		isUnavailableOffline,
		coverStyle: {
			filter: isUnavailableOffline ? 'grayscale(1)' : 'grayscale(0)',
			opacity: isUnavailableOffline ? 0.5 : 1,
		},
		blockIfUnavailable,
	};
}
